// src/components/dashboards/AnalyticsDashboard.tsx
// Two-tab analytics dashboard: Cohort Overview + Molecular & Treatment.

import React, { useCallback, useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Typography,
  alpha,
  useTheme,
  Alert,
  Button,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import useAnalytics from "hooks/useAnalytics";
import { useEnvironmentFilter } from "hooks/useEnvironmentFilter";
import { useTranslation } from "../../i18n";
import ExportDialog from "./ExportDialog";
import { useDashboardInk } from "./dashboardInk";
import { athenaColors } from "../../theme/palette";
import {
  CohortSummaryCards,
  CancerTypeBar,
  DemographicsPanel,
  StagingSunburst,
  MutationPrevalenceBar,
  BiomarkerRadar,
  TreatmentTreemap,
  EventTimeline,
  DataCompletenessBar,
} from "./charts";

// ---------------------------------------------------------------------------
// Chart container wrapper — consistent glass styling per chart
// ---------------------------------------------------------------------------
const ChartCard: React.FC<{ children: React.ReactNode; minH?: number }> = ({
  children,
  minH,
}) => {
  const theme = useTheme();
  const ink = useDashboardInk();
  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2.5 },
        borderRadius: 2,
        background: ink.dark
          ? ink.panel
          : alpha(theme.palette.background.paper, 0.45),
        border: `1px solid ${
          ink.dark ? ink.panelBorder : alpha(theme.palette.divider, 0.15)
        }`,
        backdropFilter: "blur(8px)",
        transition: "border-color 0.2s ease",
        overflow: "hidden",
        minHeight: minH,
        "&:hover": {
          borderColor: ink.dark
            ? alpha(athenaColors.periwinkle, 0.45)
            : alpha(theme.palette.divider, 0.35),
        },
      }}
    >
      {children}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
interface AnalyticsDashboardProps {
  hideSummaryCards?: boolean;
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ hideSummaryCards }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const ink = useDashboardInk();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = useState(0);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportPatientIds, setExportPatientIds] = useState<number[] | undefined>(undefined);

  // Environment filter — scopes all analytics
  const { selectedIds: envIds, isFiltered: envIsFiltered } = useEnvironmentFilter();

  const {
    cohort,
    demographics,
    staging,
    mutations,
    biomarkers,
    treatments,
    timeline,
    completeness,
    loading,
    error,
  } = useAnalytics(envIds);

  /** Open export dialog for specific patient IDs (from NL query) */
  const handleExportSelected = useCallback((ids: number[]) => {
    setExportPatientIds(ids);
    setExportOpen(true);
  }, []);

  /** Open export dialog for all patients */
  const handleExportAll = useCallback(() => {
    setExportPatientIds(undefined);
    setExportOpen(true);
  }, []);

  if (error) {
    return (
      <Alert severity="warning" sx={{ m: 2, "& .MuiAlert-message": { color: "warning.light" } }}>
        {error}
      </Alert>
    );
  }

  const hasPatients = (cohort?.total_patients ?? 0) > 0;
  const hasDocuments = (cohort?.total_documents ?? 0) > 0;
  // Charts require documents to have meaningful data
  const hasChartData = hasPatients && hasDocuments;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 2 } }}>
      {/* KPI row — hidden when rendered externally in the hero band */}
      {!hideSummaryCards && <CohortSummaryCards data={cohort} loading={loading} />}

      {/* Empty state: no patients at all */}
      {!loading && !hasPatients && (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            px: 3,
            borderRadius: 2,
            background: alpha(theme.palette.background.paper, 0.45),
            border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
            backdropFilter: "blur(8px)",
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: ink.primary }}
          >
            {t("dashboard.empty_analytics.title", "Analytics will appear here")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: ink.secondary,
              maxWidth: 480,
              mx: "auto",
            }}
          >
            {t(
              "dashboard.empty_analytics.description",
              "Create patients and upload clinical documents to see cohort demographics, staging distribution, mutation prevalence, treatment analytics, and more."
            )}
          </Typography>
        </Box>
      )}

      {/* Empty state: patients exist but no documents uploaded yet */}
      {!loading && hasPatients && !hasDocuments && (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            px: 3,
            borderRadius: 2,
            background: alpha(theme.palette.background.paper, 0.45),
            border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
            backdropFilter: "blur(8px)",
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: ink.primary }}
          >
            {t("dashboard.empty_documents.title", "Upload documents to unlock analytics")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: ink.secondary,
              maxWidth: 480,
              mx: "auto",
            }}
          >
            {t(
              "dashboard.empty_documents.description",
              "Open a patient record and upload clinical documents. Athena will automatically extract structured data and populate the charts below."
            )}
          </Typography>
        </Box>
      )}

      {/* Tab bar + chart content — single flex child so parent gap doesn't apply between them */}
      {hasChartData && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            ...(hideSummaryCards && { mt: 0.5 }),
          }}
        >
          {/* Pill-style segmented control */}
          <Box
            sx={{
              display: "inline-flex",
              background: alpha(theme.palette.primary.dark, 0.07),
              borderRadius: 2,
              p: 0.4,
              border: `1px solid ${alpha(theme.palette.primary.dark, 0.1)}`,
            }}
          >
            <Tabs
              value={tab}
              onChange={(_e, v) => setTab(v)}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons={isMobile ? "auto" : false}
              TabIndicatorProps={{ style: { display: "none" } }}
              sx={{
                minHeight: 32,
                "& .MuiTabs-flexContainer": { gap: 0.4 },
                "& .MuiTab-root": {
                  minHeight: 32,
                  px: 2,
                  py: 0.5,
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.75rem", sm: "0.82rem" },
                  color: ink.secondary,
                  transition: "all 0.18s ease",
                  "&.Mui-selected": ink.dark
                    ? {
                        color: theme.palette.common.white,
                        background: alpha(athenaColors.royalBlue, 0.75),
                        border: `1px solid ${alpha(athenaColors.periwinkle, 0.45)}`,
                        boxShadow: `0 2px 10px ${alpha(athenaColors.deepNavy, 0.45)}`,
                      }
                    : {
                        color: theme.palette.primary.dark,
                        background: alpha(theme.palette.background.paper, 0.45),
                        boxShadow: `0 1px 4px ${alpha(theme.palette.primary.dark, 0.12)}`,
                      },
                },
              }}
            >
              <Tab label={t("dashboard.tabs.cohort_overview", "Cohort Overview")} />
              <Tab label={t("dashboard.tabs.molecular_treatment", "Molecular & Treatment")} />
            </Tabs>
          </Box>
          <Tooltip title={t("dashboard.export.title", "Export Patient Data")}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
              onClick={handleExportAll}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                height: 40,
                // primary.main is deepNavy: a navy outline and navy label on a
                // navy ground, which is how this button disappeared entirely
                // on the dark surface.
                ...(ink.dark
                  ? {
                      borderColor: alpha(athenaColors.periwinkle, 0.45),
                      background: alpha(theme.palette.common.white, 0.08),
                      color: theme.palette.common.white,
                      "&:hover": {
                        borderColor: athenaColors.periwinkle,
                        background: alpha(athenaColors.periwinkle, 0.18),
                        color: theme.palette.common.white,
                      },
                    }
                  : {
                      borderColor: alpha(theme.palette.primary.main, 0.45),
                      background: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main,
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        background: alpha(theme.palette.primary.main, 0.15),
                        color: theme.palette.primary.dark,
                      },
                    }),
              }}
            >
              {t("dashboard.export.download", "Download")}
            </Button>
          </Tooltip>
        </Box>

        {/* Tab 0 — Cohort Overview */}
        {tab === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
              <ChartCard><CancerTypeBar data={cohort?.domains} loading={loading} /></ChartCard>
              <ChartCard><DemographicsPanel data={demographics} loading={loading} /></ChartCard>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
              <ChartCard><StagingSunburst data={staging} loading={loading} /></ChartCard>
              <ChartCard><DataCompletenessBar data={completeness} loading={loading} /></ChartCard>
            </Box>
          </Box>
        )}

        {/* Tab 1 — Molecular & Treatment */}
        {tab === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
              <ChartCard><MutationPrevalenceBar data={mutations} loading={loading} /></ChartCard>
              <ChartCard><BiomarkerRadar data={biomarkers} loading={loading} /></ChartCard>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
              <ChartCard><TreatmentTreemap data={treatments} loading={loading} /></ChartCard>
              <ChartCard><EventTimeline data={timeline} loading={loading} /></ChartCard>
            </Box>
          </Box>
        )}
      </Box>
      )} {/* end hasChartData column wrapper */}

      {/* Export Dialog */}
      <ExportDialog
        open={exportOpen}
        onClose={() => {
          setExportOpen(false);
          setExportPatientIds(undefined);
        }}
        patientIds={exportPatientIds}
        defaultEnvironmentIds={envIsFiltered ? envIds : undefined}
      />
    </Box>
  );
};

export default AnalyticsDashboard;
